import React from 'react';
import { create } from 'zustand';

import {
  StripeV2SubscriptionPrice,
  StripeV2CoinsPrice,
} from '@/services/stripe';

type ModalParamsType = {
  paywall: {
    stripePlans: StripeV2SubscriptionPrice[] | undefined;
    billingPortalLink?: string;
  };
  buyCoinsPaywall: {
    product: StripeV2CoinsPrice;
  };
  coinsPaywall: undefined;
  upsale: undefined;
};
type ModalNameType = keyof ModalParamsType;
interface ModalType {
  name: ModalNameType;
  params?: ModalParamsType[ModalNameType];
}

const useAppModalStore = create<{
  visibleModals: ModalType[];
  showModal: (
    modalName: ModalNameType,
    params: ModalParamsType[ModalNameType],
  ) => void;
  hideModal: (appModalName: ModalNameType) => void;
}>((set) => ({
  visibleModals: [],
  showModal: (
    name: ModalNameType,
    params?: ModalParamsType[ModalNameType],
  ): void =>
    set((state) => ({
      visibleModals: [
        ...state.visibleModals,
        {
          name,
          params,
        },
      ],
    })),
  hideModal: (appModalName: ModalNameType): void =>
    set((state) => ({
      visibleModals: state.visibleModals.filter(
        (modal) => modal.name !== appModalName,
      ),
    })),
}));

export const useAppModals = <T extends ModalNameType>(name: T) => {
  const { visibleModals, showModal, hideModal } = useAppModalStore(
    (state) => state,
  );

  const isVisible = visibleModals.some((modal) => modal.name === name);
  const params: ModalParamsType[T] | undefined = visibleModals.find(
    (modal) => modal.name === name,
  )?.params as ModalParamsType[T] | undefined;

  const show = React.useCallback(
    (params?: ModalParamsType[T]) => {
      showModal(name, params);
    },
    [name, showModal],
  );

  const hide = React.useCallback(() => {
    hideModal(name);
  }, [name, hideModal]);

  return {
    show,
    hide,
    isVisible,
    params,
  };
};
